import { Empty, List, message, Spin } from 'antd'
import CantileverButton, { TrashButton } from 'components/CantileverButton'
import CantileverCard from 'components/CantileverCard'
import SearchBar from 'components/SearchBar'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { PermissionsGate } from 'features/permissions'
import { useRolodexEntries, useUserListing } from 'features/rolodex'
import { CirclePlus } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  PERMISSION_CANTILEVER_CORE_ADMIN,
  RolodexEntryType,
  RolodexUserMetadata,
  TaggableUser,
} from 'types'
import { v4 as uuid } from 'uuid'
interface TaggableUsersCardProps {
  entityType:
    | typeof RolodexEntryType.ORGANIZATION
    | typeof RolodexEntryType.SITE
  entityID: string | undefined
}

export const TaggableUsersCard: React.FC<TaggableUsersCardProps> = ({
  entityType,
  entityID,
}) => {
  const { rolodexPut, rolodexPatch } = useRolodexEdit()
  const [messageApi, contextHolder] = message.useMessage()

  const [taggableUsers, setTaggableUsers] = useState<TaggableUser[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  const { data: allUsers = [], isLoading: isLoadingAllUsers } = useUserListing()

  const { sites } = useRolodexEntries(
    entityType === RolodexEntryType.SITE && entityID
      ? {
          type: RolodexEntryType.SITE,
          ids: [entityID],
        }
      : undefined
  )

  const orgId = sites[0]?.tags.organization

  const tags = useMemo(() => {
    return {
      [entityType]: entityID,
      ...(entityType === RolodexEntryType.SITE && orgId
        ? { [RolodexEntryType.ORGANIZATION]: orgId }
        : {}),
    }
  }, [entityID, entityType, orgId])

  const { userLists, isLoading, forceRefresh } = useRolodexEntries(
    tags
      ? {
          type: RolodexEntryType.USER_LIST,
          tags: tags,
          limit: 1,
        }
      : undefined
  )

  const userListEntry = userLists[0]

  useEffect(() => {
    if (userListEntry) {
      setTaggableUsers(userListEntry.data.users || [])
    } else {
      setTaggableUsers([])
    }
  }, [userListEntry])

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return []
    }

    const normalizedQuery = searchQuery.toLowerCase()
    const filtered = allUsers.filter((user) => {
      const name = user.name?.toLowerCase() || ''
      const email = user.email?.toLowerCase() || ''
      return name.includes(normalizedQuery) || email.includes(normalizedQuery)
    })

    return filtered.slice(0, 10)
  }, [searchQuery, allUsers])

  const saveUsers = useCallback(
    async (users: TaggableUser[]) => {
      try {
        if (userListEntry) {
          await rolodexPatch([{ id: userListEntry.id, data: { users } }])
        } else {
          const entityPrefix =
            entityType === RolodexEntryType.ORGANIZATION ? 'org' : 'site'
          const newEntryId = `user-list-${entityPrefix}-${entityID}-${uuid().slice(0, 8)}`

          await rolodexPut([
            {
              id: newEntryId,
              type: RolodexEntryType.USER_LIST,
              data: { users },
              tags: tags,
            },
          ])
        }
        await forceRefresh()
      } catch (err) {
        console.error('Error saving users:', err)
        messageApi.error('Failed to save allowed users list')
      }
    },
    [
      userListEntry,
      entityID,
      entityType,
      tags,
      rolodexPut,
      rolodexPatch,
      messageApi,
      forceRefresh,
    ]
  )

  const handleAddUser = useCallback(
    async (user: RolodexUserMetadata) => {
      const simpleUser: TaggableUser = {
        id: user.id,
        name: user.name,
        email: user.email,
      }
      if (taggableUsers.some((u) => u.id === simpleUser.id)) {
        messageApi.warning('User already in the allowed list')
        return
      }
      const updatedUsers = [...taggableUsers, simpleUser]
      setTaggableUsers(updatedUsers)
      setSearchQuery('')
      await saveUsers(updatedUsers)
    },
    [taggableUsers, messageApi, saveUsers]
  )

  const handleRemoveUser = useCallback(
    async (userId: string) => {
      const updatedUsers = taggableUsers.filter((u) => u.id !== userId)
      setTaggableUsers(updatedUsers)
      await saveUsers(updatedUsers)
    },
    [taggableUsers, saveUsers]
  )

  return (
    <CantileverCard>
      {contextHolder}
      <CantileverCard.CardHeader leadingContent='Taggable Users for @ Mentions' />
      <CantileverCard.Section topPadding bottomPadding>
        <PermissionsGate requires={[PERMISSION_CANTILEVER_CORE_ADMIN]}>
          <SearchBar
            placeholder='Search users by name or email...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ marginBottom: '1rem' }}
          />

          {!isLoadingAllUsers && searchResults.length > 0 && (
            <List
              dataSource={searchResults}
              renderItem={(user) => (
                <List.Item
                  actions={[
                    <CantileverButton
                      key='add'
                      ghost
                      primary
                      onClick={() => handleAddUser(user)}>
                      <CirclePlus width='1rem' />
                      Add
                    </CantileverButton>,
                  ]}>
                  <List.Item.Meta
                    title={user.name || user.email}
                    description={user.name ? user.email : undefined}
                  />
                </List.Item>
              )}
            />
          )}
        </PermissionsGate>

        {isLoading ? (
          <Spin />
        ) : taggableUsers.length === 0 ? (
          <Empty
            description='No users in the list. Search and add users above.'
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            dataSource={taggableUsers}
            renderItem={(user) => (
              <List.Item
                actions={[
                  <PermissionsGate
                    key='remove'
                    requires={[PERMISSION_CANTILEVER_CORE_ADMIN]}>
                    <TrashButton
                      onClick={() => handleRemoveUser(user.id)}
                      title='Remove user'
                    />
                  </PermissionsGate>,
                ]}>
                <List.Item.Meta
                  title={user.name || user.email}
                  description={user.name ? user.email : undefined}
                />
              </List.Item>
            )}
          />
        )}
      </CantileverCard.Section>
    </CantileverCard>
  )
}
